"use client"

import { Fragment, useState } from "react"
import { cn } from "@/lib/utils"
import { useInsuranceTranslation } from "@/lib/insurance/use-insurance-translation"
import { formatCHF } from "@/lib/insurance/insurers"
import { trackEvent } from "@/lib/insurance/insurance-tracking"
import type { RankedInsurer, RankStatus } from "@/lib/insurance/rank-insurers"

// How many top rows to show before "view all". The user's own row is always
// visible: if it's outside the top N it's pinned below a divider.
const COLLAPSED_COUNT = 3

// Cross-insurer comparison shown below the user's own result (AHD-8461).
// Renders as plain DOM (no framer runtime) — styling lives in coverage-form.css
// alongside the other result panels.
export function CoverageComparison({
  ranked,
  userInsurerId,
  serviceName,
  insurerId,
  serviceId,
  summary,
}: {
  ranked: RankedInsurer[]
  userInsurerId: string
  serviceName: string
  insurerId: string
  serviceId: string
  /** The best-available figure and how far it beats the visitor's own plan.
   *  Sits under this panel's subtitle because it summarises the table — it
   *  used to be a third CTA above the fold competing with "Book your
   *  check-up", and was the least-used control on the page while the rows
   *  below it got expanded four times as often (Nadjib, INS-3). */
  summary?: { title: string; sub: string }
}) {
  const { t } = useInsuranceTranslation()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  if (ranked.length === 0) return null

  const periodLabel = (period?: string): string =>
    period ? t(`gate.compare.period.${period}`) : ""

  const statusText = (r: RankedInsurer): string => {
    if (r.status === "confirmed" || r.status === "likely") {
      return `CHF ${formatCHF(r.chf)}`
    }
    return r.status === "not_covered"
      ? t("gate.compare.not_covered")
      : t("gate.compare.not_confirmed")
  }

  const renderRow = (r: RankedInsurer, i: number) => {
    const isYou = r.id === userInsurerId
    const isOpen = expanded === r.id
    const hasAmount = r.status === "confirmed" || r.status === "likely"
    return (
      <li className={cn("comparison-row", isYou && "comparison-row-you")}>
        <button
          type="button"
          className="comparison-row-btn"
          aria-expanded={isOpen}
          onClick={() => {
            const next = isOpen ? null : r.id
            setExpanded(next)
            if (next) {
              trackEvent("insurance_comparison_row_expanded", {
                compared_insurer_id: r.id,
                insurer_id: insurerId,
                service_id: serviceId,
              })
            }
          }}
        >
          <span className="comparison-rank">{i + 1}</span>
          <CompareLogo logo={r.logo} name={r.name} />
          <span className="comparison-name-col">
            <span className="comparison-name">
              {r.name}
              {isYou && (
                <span className="comparison-you-badge">
                  {t("gate.compare.you")}
                </span>
              )}
            </span>
            <span className="comparison-plan">
              {r.bestPackageName
                ? `${r.bestPackageName}${hasAmount ? ` · ${r.pct}%` : ""}`
                : statusLabel(r.status, t)}
            </span>
            {/* Their own insurer has a better tier than the one they're on —
                their row shows THEIR plan, so say the upgrade out loud. */}
            {r.betterPlan && (
              <span className="comparison-upgrade">
                {t("gate.compare.better_plan", {
                  plan: r.betterPlan.name,
                  amount: formatCHF(r.betterPlan.chf),
                })}
              </span>
            )}
          </span>
          <span
            className={cn(
              "comparison-amount",
              !hasAmount && "comparison-amount-muted"
            )}
          >
            {statusText(r)}
          </span>
          <svg
            className={cn(
              "comparison-chevron",
              isOpen && "comparison-chevron-open"
            )}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {isOpen && (
          <div className="comparison-detail">
            <div className="comparison-detail-head">
              {t("gate.compare.all_plans", {
                count: String(r.packages.length),
                insurer: r.name,
              })}
            </div>
            <ul className="comparison-plan-list">
              {r.packages.map((p, pi) => {
                const planHasAmount =
                  p.status === "confirmed" || p.status === "likely"
                return (
                  <li key={p.id} className="comparison-plan-row">
                    <span className="comparison-plan-name">
                      {p.name}
                      {pi === 0 && planHasAmount && r.packages.length > 1 && (
                        <span className="comparison-best-badge">
                          {t("gate.compare.best")}
                        </span>
                      )}
                      {p.status === "likely" && (
                        <span className="comparison-likely-badge">
                          {t("gate.compare.likely")}
                        </span>
                      )}
                    </span>
                    <span className="comparison-plan-meta">
                      {periodLabel(p.period)}
                    </span>
                    <span
                      className={cn(
                        "comparison-plan-amount",
                        !planHasAmount && "comparison-amount-muted"
                      )}
                    >
                      {planHasAmount
                        ? `CHF ${formatCHF(p.chf)}`
                        : statusLabel(p.status, t)}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </li>
    )
  }

  // Collapse the long list to the top few, but always keep the user's own row
  // visible — pinned below a divider (with its real rank) when it falls outside
  // the top slice, so "where do I stand" never requires expanding.
  const userIdx = ranked.findIndex((r) => r.id === userInsurerId)
  const truncatable = ranked.length > COLLAPSED_COUNT
  const visible: { r: RankedInsurer; i: number; divider?: boolean }[] =
    !truncatable || showAll
      ? ranked.map((r, i) => ({ r, i }))
      : ranked.slice(0, COLLAPSED_COUNT).map((r, i) => ({ r, i }))
  if (truncatable && !showAll && userIdx >= COLLAPSED_COUNT) {
    visible.push({ r: ranked[userIdx], i: userIdx, divider: true })
  }

  return (
    <div className="comparison-panel">
      <div className="comparison-header">
        <div className="comparison-title">{t("gate.compare.title")}</div>
        {/* One caption line, not a second title/subtitle pair under the
            first — stacked, the two pairs read as repeated furniture. */}
        <div className="comparison-subtitle">
          {t("gate.compare.subtitle", { service: serviceName })}
          {summary && (
            <>
              {" · "}
              <span className="comparison-subtitle-best">{summary.title}</span>
              {", "}
              {summary.sub}
            </>
          )}
        </div>
      </div>

      <ul className="comparison-list">
        {visible.map(({ r, i, divider }) => (
          <Fragment key={r.id}>
            {divider && (
              <li className="comparison-skip" aria-hidden="true">
                <span>···</span>
              </li>
            )}
            {renderRow(r, i)}
          </Fragment>
        ))}
      </ul>

      {truncatable && (
        <button
          type="button"
          className="comparison-toggle"
          aria-expanded={showAll}
          onClick={() => {
            if (!showAll) {
              trackEvent("insurance_comparison_view_all", {
                insurer_id: insurerId,
                service_id: serviceId,
                insurer_count: ranked.length,
              })
            }
            setShowAll((v) => !v)
          }}
        >
          {showAll
            ? t("gate.compare.show_less")
            : t("gate.compare.view_all", { count: String(ranked.length) })}
        </button>
      )}

      <p className="comparison-footnote">{t("gate.compare.footnote")}</p>
    </div>
  )
}

function statusLabel(status: RankStatus, t: (k: string) => string): string {
  return status === "not_covered"
    ? t("gate.compare.not_covered")
    : status === "unknown"
      ? t("gate.compare.not_confirmed")
      : ""
}

function CompareLogo({
  logo,
  name,
}: {
  logo: RankedInsurer["logo"]
  name: string
}) {
  if (logo?.url) {
    return (
      <span className="comparison-logo">
        {/* eslint-disable-next-line @next/next/no-img-element -- CMS host-relative logo, same as result panel */}
        <img src={logo.url} alt={logo.alt || name} />
      </span>
    )
  }
  return (
    <span className="comparison-logo comparison-logo-fallback">
      {name.charAt(0)}
    </span>
  )
}
